'use client';

import { Button, Stack, Theme, Typography, useBreakpoints } from '@castlery/fortress';
import { ArrowLeft, ArrowRight } from '@castlery/fortress/Icons';
import { DtStack } from '@castlery/modules-tracking-components';
import { storyblokEditable } from '@storyblok/react/rsc';
import { useEffect, useRef, useState } from 'react';
import { hasRichText } from '../../utils/rich-text-utils';
import { RichTextTypography } from '../component-v1/components';
import { UGCListing, UGCListingProps } from '../component-v1/ugc/ugc-listing';
import { SocialUgcModal } from './components/social-ugc-modal';

interface SocialUGCProps {
  blok: {
    header: string;
    text: string;
    background_color: string;
    navigation_display: boolean;
    items: UGCListingProps[];
    header_color: string;
    nums_in_line: '4' | '6';
    _uid: string;
  };
  ref?: React.RefObject<HTMLDivElement>;
}

const SocialUGC = ({ blok, ref }: SocialUGCProps) => {
  const defaultRef = useRef<HTMLDivElement>(null);
  const socialUgcRootRef = ref || defaultRef;
  const { header, text, background_color, navigation_display, items, header_color, nums_in_line, _uid } = blok;
  const containerRef = useRef<HTMLDivElement>(null);
  const itemCount = items.length;
  const { desktop } = useBreakpoints();

  const generalWidth = desktop ? (nums_in_line === '4' ? 420 : 290) : nums_in_line === '4' ? 256 : 252;
  const generalWidthWithoutMargin = desktop ? (nums_in_line === '4' ? 404 : 278) : 240;

  const [modalOpen, setModalOpen] = useState(false);

  // 克隆结构：[cloneLast, ...original, cloneFirst]
  const loopedItems = [...items, ...items, ...items];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [clickedIndex, setClickedIndex] = useState(0);
  const [firstEnter, setFirstEnter] = useState(false);

  const handlePrev = () => {
    setCurrentIndex((prev) => prev - 1);
    setFirstEnter(true);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => prev + 1);
    setFirstEnter(true);
  };

  useEffect(() => {
    if (itemCount > 0 && containerRef.current) {
      setCurrentIndex(itemCount);
      const transformValue = `translateX(-${itemCount * generalWidth}px)`;
      containerRef.current.style.transition = 'none';
      containerRef.current.style.transform = transformValue;
      // 强制重绘
      const _ = containerRef.current.offsetHeight;
      containerRef.current.style.transition = 'transform 0.3s ease-in-out';
    }
  }, [itemCount, generalWidth]);

  useEffect(() => {
    if (containerRef.current) {
      if (currentIndex === itemCount * 2 || currentIndex === 0) {
        if (firstEnter) {
          setTimeout(() => {
            const transformValue = `translateX(-${itemCount * generalWidth}px)`;
            if (containerRef.current) {
              containerRef.current.style.transition = 'none';
              containerRef.current.style.transform = transformValue;
              // 强制重绘
              const __ = containerRef.current.offsetHeight;
              window.setTimeout(() => {
                if (containerRef.current) {
                  containerRef.current.style.transition = 'transform 0.3s ease-in-out';
                }
              }, 50);
            }
            setCurrentIndex(itemCount);
          }, 500);
        } else {
          const transformValue = `translateX(-${itemCount * generalWidth}px)`;
          if (containerRef.current) {
            containerRef.current.style.transition = 'none';
            containerRef.current.style.transform = transformValue;
            // 强制重绘
            const __ = containerRef.current.offsetHeight;
            window.setTimeout(() => {
              if (containerRef.current) {
                containerRef.current.style.transition = 'transform 0.3s ease-in-out';
              }
            }, 50);
          }
          setCurrentIndex(itemCount);
        }
      }
    }
  }, [currentIndex, itemCount, generalWidth]);

  useEffect(() => {
    if (containerRef.current) {
      const transformValue = `translateX(-${currentIndex * generalWidth}px)`;
      containerRef.current.style.transform = transformValue;
    }
  }, [currentIndex, generalWidth]);

  return (
    <DtStack useImpression {...storyblokEditable(blok)} componentName="social-ugc" uid={_uid} key={_uid}>
      <Stack
        ref={socialUgcRootRef}
        sx={(theme) => ({
          width: '100%',
          backgroundColor: background_color,
          padding: desktop ? `60px 0` : `32px 0`,
          px: desktop && nums_in_line === '4' ? '32px' : 0,
        })}
      >
        <Stack alignItems="center">
          <Typography
            level="h2"
            sx={(theme) => ({
              color: header_color || theme.palette.brand.maroonVelvet[500],
              marginBottom: '8px',
              marginLeft: desktop ? '32px' : '24px',
              marginRight: desktop ? '32px' : '24px',
            })}
          >
            {header}
          </Typography>
          {hasRichText(text) && (
            <RichTextTypography
              level="body1"
              sx={(theme) => ({
                color: `${theme.palette.brand.maroonVelvet[500]}`,
                marginBottom: desktop ? '32px' : '20px',
                marginLeft: desktop ? '32px' : '24px',
                marginRight: desktop ? '32px' : '24px',
                p: {
                  textAlign: 'center',
                },
                a: {
                  color: `${theme.palette.brand.terracotta[500]} !important`,
                  textDecorationColor: `${theme.palette.brand.terracotta[500]} !important`,
                  span: {
                    color: `${theme.palette.brand.terracotta[500]} !important`,
                    '&:hover': {
                      color: `${theme.palette.brand.terracotta[600]} !important`,
                    },
                  },
                  '&:hover': {
                    color: `${theme.palette.brand.terracotta[600]} !important`,
                    textDecorationColor: `${theme.palette.brand.terracotta[600]} !important`,
                  },
                },
              })}
              description={text}
            />
          )}
        </Stack>
        <Stack sx={{ overflow: 'hidden' }}>
          <Stack
            ref={containerRef}
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'row',
              width: 'auto',
              transition: 'transform 0.3s ease-in-out',
              gap: nums_in_line === '4' ? '16px' : '12px',
            })}
          >
            {loopedItems.map((item, index) => (
              <Stack
                key={`ugc-${index}`}
                sx={(theme) => ({
                  width: generalWidthWithoutMargin,
                  height: generalWidthWithoutMargin,
                  flexShrink: 0,
                  cursor: 'pointer',
                })}
                onClick={() => {
                  setModalOpen(true);
                  setClickedIndex(index);
                }}
              >
                <UGCListing
                  blok={item}
                  enableLink={false}
                  sizes={[
                    '0.8-xs',
                    `${nums_in_line === '4' ? 0.25 : 0.18}-md`,
                    `${nums_in_line === '4' ? 0.25 : 0.18}-lg`,
                    `${(nums_in_line === '4' ? 0.25 : 0.18) * 0.8}-xl`,
                  ]}
                />
              </Stack>
            ))}
          </Stack>

          {navigation_display && (
            <Stack
              sx={(theme) => ({
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                mt: desktop ? '32px' : '16px',
                paddingRight: desktop ? '32px' : '24px',
              })}
            >
              <Button
                aria-label="Previous"
                sx={(theme: Theme) => ({
                  width: desktop ? 64 : `${40}px !important`,
                  height: desktop ? 64 : `${40}px !important`,
                  padding: 0,
                  minHeight: desktop ? 64 : `${40}px !important`,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.brand.warmLinen[500],
                  boxShadow: 'none',
                  marginRight: desktop ? '24px' : '16px',
                  border: 'none !important',
                  '&:hover': {
                    backgroundColor: theme.palette.brand.warmLinen[500],
                    boxShadow: 'none !important',
                  },
                  '&:focus': {
                    boxShadow: 'none !important',
                  },
                  '&:active': {
                    boxShadow: 'none !important',
                  },
                })}
                onClick={handlePrev}
              >
                <ArrowLeft
                  sx={(theme: Theme) => ({
                    color: theme.palette.brand.burntOrange[500],
                  })}
                />
              </Button>
              <Button
                aria-label="Next"
                sx={(theme: Theme) => ({
                  width: desktop ? 64 : `${40}px !important`,
                  height: desktop ? 64 : `${40}px !important`,
                  padding: 0,
                  minHeight: desktop ? 64 : `${40}px !important`,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.brand.warmLinen[500],
                  boxShadow: 'none',
                  border: 'none !important',
                  '&:hover': {
                    backgroundColor: theme.palette.brand.warmLinen[500],
                    boxShadow: 'none !important',
                  },
                  '&:focus': {
                    boxShadow: 'none !important',
                  },
                  '&:active': {
                    boxShadow: 'none !important',
                  },
                })}
                onClick={handleNext}
              >
                <ArrowRight
                  sx={(theme: Theme) => ({
                    color: theme.palette.brand.burntOrange[500],
                  })}
                />
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
      <SocialUgcModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        ugcData={items}
        initialIndex={clickedIndex % itemCount}
      />
    </DtStack>
  );
};

export { SocialUGC };
